import { parseFriendlyConversationId } from '@provision/formatting-utils';
import { isNullish } from 'remeda';
import { timeZoneOptions } from 'src/constants/timezone';
import { CUSTOM_VIEW_GROUPS } from 'src/graphql/entities/custom-view/constants';
import { mapServiceLevelFilter } from 'src/graphql/entities/custom-view/custom-view-config/service-level-filter';
import {
  CustomViewFormConditionCustomData,
  CustomViewFormConditionField,
  UIType,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';
import { ConditionInput, ConditionOperator, EntityNames } from 'src/graphql/types';
import { SupportedActionQueries } from 'src/pages/Settings/rules/use-rules-engine';
import { isUUID } from 'src/utils/uuid';
import {
  ConversationAttribute,
  ConversationAttributeAccessors,
  ConversationAttributeType,
} from './constants';
import { conversationAttributeConfigMap } from './displaying';

export const conversationFilterAttributesList = [
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.partnerId)!,
    attributeLabel: 'Partner',
    pluralLabel: 'partners',
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getPartner,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.timeZone)!,
    uiType: UIType.List,
    options: timeZoneOptions,
    fieldOptions: timeZoneOptions,
  },
  conversationAttributeConfigMap.get(ConversationAttribute.id)!,
  conversationAttributeConfigMap.get(ConversationAttribute.subject)!,
  {
    attribute: ConversationAttribute.label,
    attributeLabel: 'Label',
    pluralLabel: 'labels',
    uiType: UIType.List,
    group: CUSTOM_VIEW_GROUPS.Conversation,
    optionsQuery: SupportedActionQueries.getAllLabels,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.conversationStatus)!,
    pluralLabel: 'statuses',
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getConversationStatus,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.createdAt)!,
    uiType: UIType.Date,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.type)!,
    pluralLabel: 'types',
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getConversationType,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.conversationUrgency)!,
    pluralLabel: 'urgencies',
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getEnabledConversationUrgencies,
  },
  {
    attribute: ConversationAttribute.assignee,
    attributeLabel: 'Assignee',
    uiType: UIType.List,
    pluralLabel: 'assignees',
    group: CUSTOM_VIEW_GROUPS.Conversation,
    optionsQuery: SupportedActionQueries.getPlatformUser,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.assigneeGroup)!,
    attributeLabel: 'Assignee Group',
    pluralLabel: 'assignee groups',
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getEnabledConversationAssigneeGroups,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.serviceLevel)!,
    uiType: UIType.List,
    pluralLabel: 'service levels',
    optionsQuery: SupportedActionQueries.getServiceLevelIdOptions,
  },
  {
    attribute: ConversationAttribute.sla,
    attributeLabel: 'SLA',
    uiType: UIType.List,
    group: CUSTOM_VIEW_GROUPS.Site,
    optionsQuery: SupportedActionQueries.getSla,
  },
  conversationAttributeConfigMap.get(ConversationAttribute.snoozeUntil)!,
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.lastMessageReceivedAt)!,
    uiType: UIType.Date,
  },
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.clientSegment)!,
    pluralLabel: 'client segments',
    uiType: UIType.List,
    attributeLabel: 'Client Segment',
    optionsQuery: SupportedActionQueries.getBrand,
  },
  conversationAttributeConfigMap.get(ConversationAttribute.addressName)!,
  {
    ...conversationAttributeConfigMap.get(ConversationAttribute.requesterId)!,
    uiType: UIType.List,
    attributeLabel: 'Requester',
    pluralLabel: 'requesters',
    optionsQuery: SupportedActionQueries.getConversationParticipantOptions,
  },
  {
    attribute: ConversationAttribute.conversationAddressLabel,
    attributeLabel: 'Site Label',
    pluralLabel: 'site labels',
    uiType: UIType.List,
    group: CUSTOM_VIEW_GROUPS.Site,
    optionsQuery: SupportedActionQueries.getAllLabels,
  },
  {
    attribute: ConversationAttribute.userParticipant,
    attributeLabel: 'User Participant',
    uiType: UIType.List,
    pluralLabel: 'user participants',
    group: CUSTOM_VIEW_GROUPS.Conversation,
    optionsQuery: SupportedActionQueries.getPlatformUser,
  },
  {
    attribute: ConversationAttribute.deleted,
    attributeLabel: 'Deleted',
    uiType: UIType.Flag,
    group: CUSTOM_VIEW_GROUPS.Conversation,
    allowedRules: [UserGroupRules.AccessToRestoreDeletedConversations],
    queryParams: { includeDeleted: true },
  },
].map((i) => ({
  options: [],
  fieldOptions: [],
  ...i,
  accessor: ConversationAttributeAccessors[i.attribute].filter,
  value: i.attribute,
  label: i.attributeLabel,
  rootEntity: EntityNames.Conversation as const,
}));

export const conversationFilterAttributesMap = new Map<
  ConversationAttributeType,
  CustomViewFormConditionField
>(conversationFilterAttributesList.map((i) => [i.attribute, i]));

export const CONVERSATION_FILTER_MAPPER: Record<
  string,
  (data: CustomViewFormConditionCustomData) => ConditionInput | ConditionInput[]
> = {
  [ConversationAttribute.id]: (data: CustomViewFormConditionCustomData) => {
    // if value is a valid number - it is workspaceConversationId without a partner
    // so using it directly without parsing and partner filtering
    if (data.value && Number(data.value)) {
      return [
        {
          attribute: 'workspaceConversationId',
          value: data.value,
          operator: data.operator,
          logicalOperator: data.logicalOperator,
        },
      ];
    }

    // this allows users to filter by uuid instead of friendly conversation id
    if (isUUID(data.value as string)) {
      return [
        {
          attribute: 'conversationId',
          value: data.value,
          operator: data.operator,
          logicalOperator: data.logicalOperator,
        },
      ];
    }

    const { workspaceConversationId, partnerId } = parseFriendlyConversationId(
      data.value as string,
    );

    const filters = [];

    if (!isNullish(partnerId)) {
      filters.push({
        attribute: 'partnerId',
        value: String(partnerId),
        operator: ConditionOperator.Equals,
        logicalOperator: 'and',
      });
    }

    if (workspaceConversationId && Number(workspaceConversationId)) {
      filters.push({
        attribute: 'workspaceConversationId',
        value: workspaceConversationId,
        operator: data.operator,
        logicalOperator: data.logicalOperator,
      });
    }
    return filters;
  },
  [ConversationAttributeAccessors[ConversationAttribute.addressName].filter]: (
    data: CustomViewFormConditionCustomData,
  ) => {
    // In case of filtering by business address name = empty/not empty it is expected to return conversations where no
    // addresses [not] defined at all. That's why it is filtered by `associatedAddresses/id` in such cases to avoid
    // returning related address entity at all.
    if (data.operator === 'is_empty' || data.operator === 'is_not_empty') {
      return [
        {
          ...data,
          attribute: 'associatedAddresses/id',
        },
      ];
    }
    return [
      {
        ...data,
        attribute: ConversationAttributeAccessors[ConversationAttribute.addressName].filter,
      },
    ];
  },
  [ConversationAttributeAccessors[ConversationAttribute.serviceLevel].filter]: (
    data: CustomViewFormConditionCustomData,
  ) =>
    mapServiceLevelFilter(
      data,
      ConversationAttributeAccessors[ConversationAttribute.serviceLevel].filter,
      ConversationAttributeAccessors[ConversationAttribute.addressCppExpDate].filter,
    ),
};
